import { useState } from 'react';
import { Sparkles, ArrowRight, Check, Loader2 } from 'lucide-react';
import type { OnboardingPreferences } from '../api';
import { Autocomplete } from './ui';

interface OnboardingFlowProps {
  firstName: string;
  isLoading: boolean;
  onComplete: (preferences: OnboardingPreferences) => Promise<{ success: boolean; error?: string }>;
  onSkip: () => void;
}

type OnboardingStep = 'welcome' | 'preferences';

export function OnboardingFlow({
  firstName,
  isLoading,
  onComplete,
  onSkip,
}: OnboardingFlowProps) {
  const [step, setStep] = useState<OnboardingStep>('welcome');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleComplete = async () => {
    setError(null);
    const preferences: OnboardingPreferences = {
      timezone,
      notificationPreferences: {
        email: emailNotifications,
      },
    };

    const result = await onComplete(preferences);
    if (!result.success) {
      setError(result.error || 'Failed to save preferences');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              step === 'welcome' ? 'bg-purple-600' : 'bg-purple-300'
            }`}
          />
          <div
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              step === 'preferences' ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          />
        </div>

        {step === 'welcome' && (
          <WelcomeStep
            firstName={firstName}
            onContinue={() => setStep('preferences')}
            onSkip={onSkip}
          />
        )}

        {step === 'preferences' && (
          <PreferencesStep
            timezone={timezone}
            onTimezoneChange={setTimezone}
            emailNotifications={emailNotifications}
            onEmailNotificationsChange={setEmailNotifications}
            isLoading={isLoading}
            error={error}
            onComplete={handleComplete}
            onBack={() => setStep('welcome')}
          />
        )}
      </div>
    </div>
  );
}

interface WelcomeStepProps {
  firstName: string;
  onContinue: () => void;
  onSkip: () => void;
}

function WelcomeStep({ firstName, onContinue, onSkip }: WelcomeStepProps) {
  return (
    <div className="text-center">
      {/* Header */}
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
        <Sparkles className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
        Welcome, {firstName}!
      </h1>
      <p className="text-gray-600 text-lg mb-8 max-w-sm mx-auto">
        Let's set up your account so you can get the most out of Byte Quality Complaints.
      </p>

      {/* Features */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 text-left">
        <h3 className="font-semibold text-gray-900 mb-4">With Byte Quality Complaints you can:</h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-green-600" />
            </div>
            <span className="text-gray-700">View and respond to quality complaints</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-green-600" />
            </div>
            <span className="text-gray-700">Access quality compliance data</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-green-600" />
            </div>
            <span className="text-gray-700">Track complaint resolution status</span>
          </li>
        </ul>
      </div>

      {/* Actions */}
      <button
        onClick={onContinue}
        className="w-full py-3 px-4 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
      >
        Get Started
        <ArrowRight className="w-5 h-5" />
      </button>

      <button
        onClick={onSkip}
        className="mt-4 text-gray-500 hover:text-gray-700 text-sm transition-colors"
      >
        Skip setup
      </button>
    </div>
  );
}

interface PreferencesStepProps {
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  emailNotifications: boolean;
  onEmailNotificationsChange: (enabled: boolean) => void;
  isLoading: boolean;
  error: string | null;
  onComplete: () => void;
  onBack: () => void;
}

function PreferencesStep({
  timezone,
  onTimezoneChange,
  emailNotifications,
  onEmailNotificationsChange,
  isLoading,
  error,
  onComplete,
  onBack,
}: PreferencesStepProps) {
  const commonTimezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
  ];

  return (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Your preferences</h1>
        <p className="text-gray-600">Customize your experience</p>
      </div>

      {/* Preferences Form */}
      <div className="space-y-6 mb-8">
        {/* Timezone */}
        <Autocomplete
          label="Time zone"
          options={commonTimezones.map((tz) => ({
            value: tz,
            label: tz.replace(/_/g, ' '),
          }))}
          value={timezone}
          onChange={(v) => onTimezoneChange(v || timezone)}
          placeholder="Search or select time zone..."
          id="timezone"
        />

        {/* Email Notifications */}
        <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
          <div>
            <p className="font-medium text-gray-900">Email notifications</p>
            <p className="text-sm text-gray-500">Receive updates and alerts via email</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={emailNotifications}
            onClick={() => onEmailNotificationsChange(!emailNotifications)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              emailNotifications ? 'bg-purple-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                emailNotifications ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={onComplete}
          disabled={isLoading}
          className="flex-1 py-3 px-4 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Finish Setup
              <Check className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </>
  );
}
